import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Pages.css';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const emptyDay = () => ({ enabled: false, start_time: '09:00', end_time: '17:00' });

const PractitionerAvailability = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [days, setDays] = useState(DAYS.map(emptyDay));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const practitionerId = user?.id;

  useEffect(() => {
    if (user && user.role !== 'practitioner' && user.role !== 'admin') {
      navigate('/');
      return;
    }
    if (practitionerId) fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practitionerId]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/practitioners/${practitionerId}/availability-rules`, { headers });
      const next = DAYS.map(emptyDay);
      res.data.forEach((rule) => {
        next[rule.day_of_week] = { enabled: true, start_time: rule.start_time.slice(0, 5), end_time: rule.end_time.slice(0, 5) };
      });
      setDays(next);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const updateDay = (index, patch) => {
    setDays((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const rules = days
        .map((d, i) => ({ ...d, day_of_week: i }))
        .filter((d) => d.enabled)
        .map((d) => ({ day_of_week: d.day_of_week, start_time: d.start_time, end_time: d.end_time }));
      await axios.put(`${apiUrl}/practitioners/${practitionerId}/availability-rules`, { rules }, { headers });
      setNotice('Availability saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-container"><p className="text-muted">Loading availability...</p></div>;

  return (
    <div className="page-container" style={{ maxWidth: 640 }}>
      <div className="page-header">
        <h1>Weekly Availability</h1>
      </div>
      <p className="text-muted" style={{ marginBottom: 16 }}>
        Set the hours you're bookable each week. Clients will only see open slots inside these
        windows - and any time already booked, or blocked on your connected Google Calendar, is
        automatically excluded.
      </p>
      {error && <div className="error-message">{error}</div>}
      {notice && !error && <p className="text-muted" style={{ marginBottom: 12 }}>{notice}</p>}

      <div className="card">
        {days.map((day, i) => (
          <div key={DAYS[i]} className="flex-between" style={{ padding: '10px 0', borderBottom: i < 6 ? '1px solid #eee' : 'none', flexWrap: 'wrap', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
              <input type="checkbox" checked={day.enabled} onChange={(e) => updateDay(i, { enabled: e.target.checked })} />
              {DAYS[i]}
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="time"
                disabled={!day.enabled}
                value={day.start_time}
                onChange={(e) => updateDay(i, { start_time: e.target.value })}
              />
              <span className="text-muted">to</span>
              <input
                type="time"
                disabled={!day.enabled}
                value={day.end_time}
                onChange={(e) => updateDay(i, { end_time: e.target.value })}
              />
            </div>
          </div>
        ))}
      </div>

      <button className="btn btn-primary btn-block" style={{ marginTop: 16 }} disabled={saving} onClick={save}>
        {saving ? 'Saving...' : 'Save Availability'}
      </button>
    </div>
  );
};

export default PractitionerAvailability;
